import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Dropdown } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { supabase } from '../lib/supabaseClient';
import { compressImageForUpload } from '../lib/compressImage';
import ConfirmDialog from '../components/ConfirmDialog';
import './AdminDashboard.css';

function AdminDashboard() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentModel, setCurrentModel] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState({
    image_1: null,
    image_2: null,
    image_3: null,
    image_4: null,
  });
  const [sortBy, setSortBy] = useState('displayOrder'); // 'displayOrder' | 'name' | 'lastAdded'
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
  });

  const [formData, setFormData] = useState({
    name: '',
    gender: 'Female',
    type: 'kids',
    age: '',
    height: '',
    weight: '',
    top_size: '',
    pants_size: '',
    shoes_size: '',
    image_1: '',
    image_2: '',
    image_3: '',
    image_4: '',
  });

  useEffect(() => {
    fetchModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('models')
        .select('*')
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setModels(data || []);
    } catch (error) {
      showAlert('Error fetching models: ' + error.message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Sort models based on sortBy state (untuk tampilan saja, drag pakai display_order)
  const getSortedModels = () => {
    const sorted = [...models];
    if (sortBy === 'name') {
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'lastAdded') {
      return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return sorted.sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
  };

  // Handle drag end - simpan order ke database
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(getSortedModels());
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({ ...item, display_order: index }));
    setModels(updatedItems);

    try {
      for (const item of updatedItems) {
        await supabase
          .from('models')
          .update({ display_order: item.display_order, updated_at: new Date().toISOString() })
          .eq('id', item.id);
      }
      showAlert('Urutan berhasil disimpan!', 'success');
    } catch (error) {
      showAlert('Error menyimpan urutan: ' + error.message, 'danger');
      fetchModels();
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 3000);
  };

  const handleShowModal = (model = null) => {
    if (model) {
      setEditMode(true);
      setCurrentModel(model);
      setFormData({
        name: model.name,
        gender: model.gender,
        type: model.type,
        age: model.age,
        height: model.height,
        weight: model.weight,
        top_size: model.top_size,
        pants_size: model.pants_size,
        shoes_size: model.shoes_size,
        image_1: model.image_1 || '',
        image_2: model.image_2 || '',
        image_3: model.image_3 || '',
        image_4: model.image_4 || '',
      });
    } else {
      setEditMode(false);
      setCurrentModel(null);
      setFormData({
        name: '',
        gender: 'Female',
        type: 'kids',
        age: '',
        height: '',
        weight: '',
        top_size: '',
        pants_size: '',
        shoes_size: '',
        image_1: '',
        image_2: '',
        image_3: '',
        image_4: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setCurrentModel(null);
    setUploadedImages({
      image_1: null,
      image_2: null,
      image_3: null,
      image_4: null,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const getBucketName = () => {
    // Pilih bucket berdasarkan type saja
    if (formData.type === 'kids') {
      return 'kids';
    }
    return 'adult'; // Adult models ke bucket 'adult' (without 's')
  };

  const handleImageUpload = async (e, imageNumber) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showAlert('Please upload only JPG, PNG, or WebP images', 'warning');
      return;
    }

    try {
      setUploading(true);

      const fileToUpload = await compressImageForUpload(file);

      const bucketName = getBucketName();
      const fileExt = fileToUpload.name.split('.').pop() || 'jpg';
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to Supabase Storage
      const { error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      const imageUrl = publicUrlData.publicUrl;

      // Update form data with the new image URL
      setFormData({ 
        ...formData, 
        [`image_${imageNumber}`]: imageUrl 
      });

      // Track uploaded image for potential cleanup
      setUploadedImages({
        ...uploadedImages,
        [`image_${imageNumber}`]: filePath
      });

      showAlert(`Image ${imageNumber} uploaded successfully!`, 'success');
    } catch (error) {
      showAlert('Error uploading image: ' + error.message, 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.age || !formData.height || !formData.weight) {
      showAlert('Please fill all required fields (Name, Age, Height, Weight)', 'warning');
      return;
    }

    try {
      if (editMode) {
        // Update existing model
        const { error } = await supabase
          .from('models')
          .update({
            ...formData,
            age: parseFloat(formData.age),
            height: parseInt(formData.height),
            weight: parseFloat(formData.weight),
            updated_at: new Date().toISOString(),
          })
          .eq('id', currentModel.id);

        if (error) throw error;
        showAlert('Model updated successfully!', 'success');
      } else {
        // Create new model
        const maxOrder = models.length > 0 ? Math.max(...models.map((m) => m.display_order ?? 0)) : -1;
        const { error } = await supabase
          .from('models')
          .insert([{
            ...formData,
            age: parseFloat(formData.age),
            height: parseInt(formData.height),
            weight: parseFloat(formData.weight),
            display_order: maxOrder + 1,
          }]);

        if (error) throw error;
        showAlert('Model added successfully!', 'success');
      }

      handleCloseModal();
      fetchModels();
    } catch (error) {
      showAlert('Error saving model: ' + error.message, 'danger');
    }
  };

  const handleDelete = async (id, modelName) => {
    setConfirmDialog({
      show: true,
      title: 'Delete Model',
      message: `Are you sure you want to delete "${modelName}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('models')
            .delete()
            .eq('id', id);

          if (error) throw error;
          showAlert('Model deleted successfully!', 'success');
          fetchModels();
        } catch (error) {
          showAlert('Error deleting model: ' + error.message, 'danger');
        }
      },
      variant: 'danger',
    });
  };

  return (
    <div className="admin-dashboard">
      <Container fluid className="py-4">
        <div className="admin-header">
          <div>
            <h1 className="admin-title">Models Management</h1>
            <p className="admin-subtitle">Manage model profiles and photos</p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" id="sort-dropdown">
                <i className="bi bi-sort-down me-2"></i>
                Sort: {sortBy === 'name' ? 'Name' : sortBy === 'lastAdded' ? 'Last Added' : 'Display Order'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSortBy('displayOrder')}>
                  <i className="bi bi-list-ol me-2"></i>
                  Display Order (drag)
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('name')}>
                  <i className="bi bi-sort-alpha-down me-2"></i>
                  Sort by Name
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('lastAdded')}>
                  <i className="bi bi-clock-history me-2"></i>
                  Sort by Last Added
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add New Model
            </Button>
          </div>
        </div>

        {alert.show && (
          <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Table striped bordered hover className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th style={{ width: '60px' }}>Image</th>
                    <th>Name</th>
                    <th>Gender</th>
                    <th>Type</th>
                    <th>Age</th>
                    <th>Height (cm)</th>
                    <th>Weight (kg)</th>
                    <th>Top</th>
                    <th>Pants</th>
                    <th>Shoes</th>
                    <th style={{ width: '150px' }}>Actions</th>
                  </tr>
                </thead>
                <Droppable droppableId="models-table">
                  {(provided) => (
                    <tbody
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {models.length === 0 ? (
                        <tr>
                          <td colSpan="12" className="text-center py-4">
                            No models found. Add your first model!
                          </td>
                        </tr>
                      ) : (
                        getSortedModels().map((model, index) => (
                          <Draggable
                            key={model.id}
                            draggableId={String(model.id)}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={snapshot.isDragging ? 'dragging-row' : ''}
                              >
                                <td
                                  {...provided.dragHandleProps}
                                  className="drag-handle"
                                  title="Drag to reorder"
                                >
                                  <i className="bi bi-grip-vertical"></i>
                                </td>
                                <td>
                                  <img
                                    src={model.image_1}
                                    alt={model.name}
                                    className="model-thumb"
                                  />
                                </td>
                                <td>{model.name}</td>
                                <td>{model.gender}</td>
                                <td>
                                  <span className={`badge badge-${model.type}`}>
                                    {model.type}
                                  </span>
                                </td>
                                <td>{model.age}</td>
                                <td>{model.height}</td>
                                <td>{model.weight}</td>
                                <td>{model.top_size}</td>
                                <td>{model.pants_size}</td>
                                <td>{model.shoes_size}</td>
                                <td>
                                  <div className="action-buttons">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleShowModal(model)}
                                      title="Edit"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDelete(model.id, model.name)}
                                      title="Delete"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </div>
        )}
      </Container>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? 'Edit Model' : 'Add New Model'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter model name"
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Gender *</Form.Label>
                  <Form.Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Type *</Form.Label>
                  <Form.Select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="kids">Kids</option>
                    <option value="adult">Adult</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Age *</Form.Label>
                  <Form.Control
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. 25"
                    step="0.1"
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Height (cm) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. 165"
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Weight (kg) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. 52"
                    step="0.1"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Top Size</Form.Label>
                  <Form.Control
                    type="text"
                    name="top_size"
                    value={formData.top_size}
                    onChange={handleInputChange}
                    placeholder="e.g. S, M, L"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Pants Size</Form.Label>
                  <Form.Control
                    type="text"
                    name="pants_size"
                    value={formData.pants_size}
                    onChange={handleInputChange}
                    placeholder="e.g. 28, 30"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Shoes Size</Form.Label>
                  <Form.Control
                    type="text"
                    name="shoes_size"
                    value={formData.shoes_size}
                    onChange={handleInputChange}
                    placeholder="e.g. 38, 40"
                  />
                </Form.Group>
              </div>
            </div>

            <hr />
            <h6 className="mb-3">
              Images 
              <small className="text-muted ms-2">(Upload or paste URL)</small>
            </h6>
            <p className="text-muted small mb-3">
              <i className="bi bi-info-circle me-1"></i>
              Images will be uploaded to: <strong>{getBucketName()}</strong> bucket
            </p>
            
            {/* Image 1 */}
            <div className="row mb-3">
              <div className="col-md-12">
                <Form.Label>Image 1</Form.Label>
                <div className="image-upload-group">
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 1)}
                    disabled={uploading}
                    className="mb-2"
                  />
                  <div className="or-divider">OR</div>
                  <Form.Control
                    type="url"
                    name="image_1"
                    value={formData.image_1}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image1.jpg"
                    disabled={uploading}
                  />
                  {formData.image_1 && (
                    <div className="image-preview mt-2">
                      <img src={formData.image_1} alt="Preview 1" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Image 2 */}
            <div className="row mb-3">
              <div className="col-md-12">
                <Form.Label>Image 2</Form.Label>
                <div className="image-upload-group">
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 2)}
                    disabled={uploading}
                    className="mb-2"
                  />
                  <div className="or-divider">OR</div>
                  <Form.Control
                    type="url"
                    name="image_2"
                    value={formData.image_2}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image2.jpg"
                    disabled={uploading}
                  />
                  {formData.image_2 && (
                    <div className="image-preview mt-2">
                      <img src={formData.image_2} alt="Preview 2" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Image 3 */}
            <div className="row mb-3">
              <div className="col-md-12">
                <Form.Label>Image 3</Form.Label>
                <div className="image-upload-group">
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 3)}
                    disabled={uploading}
                    className="mb-2"
                  />
                  <div className="or-divider">OR</div>
                  <Form.Control
                    type="url"
                    name="image_3"
                    value={formData.image_3}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image3.jpg"
                    disabled={uploading}
                  />
                  {formData.image_3 && (
                    <div className="image-preview mt-2">
                      <img src={formData.image_3} alt="Preview 3" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Image 4 */}
            <div className="row mb-3">
              <div className="col-md-12">
                <Form.Label>Image 4</Form.Label>
                <div className="image-upload-group">
                  <Form.Control
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => handleImageUpload(e, 4)}
                    disabled={uploading}
                    className="mb-2"
                  />
                  <div className="or-divider">OR</div>
                  <Form.Control
                    type="url"
                    name="image_4"
                    value={formData.image_4}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image4.jpg"
                    disabled={uploading}
                  />
                  {formData.image_4 && (
                    <div className="image-preview mt-2">
                      <img src={formData.image_4} alt="Preview 4" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {uploading && (
              <div className="text-center mb-3">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </div>
                <span className="text-muted">Uploading image...</span>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editMode ? 'Update Model' : 'Add Model'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        show={confirmDialog.show}
        onHide={() => setConfirmDialog({ ...confirmDialog, show: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
      />
    </div>
  );
}

export default AdminDashboard;
